import { CurrencyCode } from './types/currency-code.type';
import { CurrencyStringFormatOptions } from './types/currency-string-format-options.type';
import { NumberLocale } from './types/number-locale.type';
import { NumberStringFormatOptions } from './types/number-string-format-options.type';

import { isDefinedString } from '../../stringUtils';

declare global {
  interface Window {
    __intlNumberUtilsLocale__?: NumberLocale; // TODO atot 2022-08-18: should have a type definition
  }
}

window.__intlNumberUtilsLocale__ = 'nb';

export const changeNumberLocale = (locale: NumberLocale) => (window.__intlNumberUtilsLocale__ = locale);

export const getNumberLocale = () => window.__intlNumberUtilsLocale__;

/**
 * Returns a formatted number string
 * @param amount The original number value
 * @param placeholder Placeholder if value is nullish
 * @param options The object with options
 * @returns The formatted number string
 */
export const toNumberString = (amount: number | undefined, placeholder = '-', options?: NumberStringFormatOptions) => {
  const defaultOptions: NumberStringFormatOptions = {
    locale: getNumberLocale(),
    maximumFractionDigits: 2,
  };

  const { locale, unit, ...numberFormatOptions } = { ...defaultOptions, ...options };

  if (amount == null) {
    return placeholder;
  }

  const numberString = new Intl.NumberFormat(locale, {
    ...numberFormatOptions,
  }).format(amount);

  // TODO: by default Intl.NumberFormat provides two options `unit` & `unitDisplay` which would allow to set a unit natively - therefor we would need to map our enums to the official values which can be found here: https://github.com/unicode-org/cldr/blob/main/common/validity/unit.xml
  if (unit == null || typeof unit !== 'string' || unit.trim() === '') {
    return numberString;
  }

  return `${numberString} ${unit}`;
};

/**
 * Returns a formatted currency string
 * @param amount The original number value
 * @param placeholder Placeholder if value is nullish
 * @param options The object with options
 * @returns The formatted currency string
 */
export const toCurrencyString = (
  amount: number | undefined,
  placeholder = '-',
  options?: CurrencyStringFormatOptions,
) => {
  const defaultOptions = {
    locale: getNumberLocale(),
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'currency',
  };

  const { locale, ...numberFormatOptions } = { ...defaultOptions, ...options };

  if (amount == null) {
    return placeholder;
  }

  if (numberFormatOptions.maximumFractionDigits < numberFormatOptions.minimumFractionDigits) {
    numberFormatOptions.minimumFractionDigits = numberFormatOptions.maximumFractionDigits;
  }

  if (
    numberFormatOptions.currency == null ||
    typeof numberFormatOptions.currency !== 'string' ||
    numberFormatOptions.currency.trim() === ''
  ) {
    numberFormatOptions.style = 'decimal';
    numberFormatOptions.currency = undefined;
  }

  return new Intl.NumberFormat(locale, {
    ...numberFormatOptions,
  }).format(amount);
};

/**
 * Receive the curreny symbol based either on the provided `locale` & `currency` or on the configured `currencyCode` and `numberLocale`
 */
export const getCurrencySymbol = (
  currency: CurrencyCode | string | undefined = undefined,
  locale = getNumberLocale(),
) => {
  let symbol;

  try {
    symbol = (0)
      .toLocaleString(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      } as CurrencyStringFormatOptions)
      .replace(/\d/g, '')
      .trim();
    return symbol;
  } catch (e) {
    return symbol;
  }
};

/**
 * Returns a formatted number string based on a fractional value
 * @param fraction The original number value as fraction
 * @param placeholder Placeholder if value is nullish
 * @param options The object with options
 * @returns The formatted number string
 */
export const toPercent = (
  fraction: number | undefined | null,
  placeholder = '-',
  options?: NumberStringFormatOptions,
) => {
  const defaultOptions: NumberStringFormatOptions = {
    locale: getNumberLocale(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  };

  const { locale, unit, ...numberFormatOptions } = { ...defaultOptions, ...options };

  if (fraction == null) {
    return placeholder;
  }

  const numberString = new Intl.NumberFormat(locale, {
    style: 'percent',
    ...numberFormatOptions,
  }).format(fraction);

  // TODO: by default Intl.NumberFormat provides two options `unit` & `unitDisplay` which would allow to set a unit natively - therefor we would need to map our enums to the official values which can be found here: https://github.com/unicode-org/cldr/blob/main/common/validity/unit.xml
  if (unit == null || typeof unit !== 'string' || unit.trim() === '') {
    return numberString;
  }

  return `${numberString} ${unit}`;
};

/**
 * ###########################################################################################################################
 * Some helper fn to handle undefined
 */

export const toNumberStringOrUndefined = (
  amount?: number,
  placeholder?: string,
  options?: NumberStringFormatOptions,
) => {
  if (amount === null) return undefined;
  if (amount === undefined) return undefined;
  return toNumberString(amount, placeholder, options);
};

export const toCurrencyStringOrUndefined = (
  amount?: number,
  placeholder?: string,
  options?: CurrencyStringFormatOptions,
) => {
  if (amount === null) return undefined;
  if (amount === undefined) return undefined;
  try {
    return toCurrencyString(amount, placeholder, options);
  } catch (e) {
    return undefined;
  }
};

export const getCurrencySymbolOrUndefined = (currency: CurrencyCode | string | undefined, locale?: NumberLocale) => {
  if (!isDefinedString(currency)) return undefined;
  if (!isDefinedString(getCurrencySymbol(currency, locale))) return undefined;
  return getCurrencySymbol(currency, locale);
};
